import { useState, useEffect } from "react";
import api from "@/lib/api";
import CrudTable from "@/components/CrudTable";
import { inr, pct } from "@/lib/format";
import { Landmark } from "lucide-react";

export default function FundArrangements() {
  const [rel, setRel] = useState([]);
  const loadRel = () => api.get("/funds-relationships").then((r) => setRel(r.data)).catch(() => {});
  useEffect(() => { loadRel(); }, []);

  const columns = [
    { key: "type", label: "Type" },
    { key: "source", label: "Source / Bank" },
    { key: "purpose", label: "Purpose" },
    { key: "amount", label: "Amount", type: "currency" },
    { key: "interest_rate", label: "Rate", render: (r) => <span className="tabular-nums">{r.interest_rate ? `${r.interest_rate}%` : "—"}</span> },
    { key: "applied_date", label: "Applied" },
    { key: "status", label: "Status", type: "badge" },
    { key: "renewal_date", label: "Renewal" },
  ];
  const fields = [
    { key: "type", label: "Type", type: "select", required: true, options: ["Working Capital Loan", "Term Loan", "Overdraft", "Cash Credit", "Investor Funding", "Other"] },
    { key: "source", label: "Source / Bank / Investor", type: "text", required: true },
    { key: "purpose", label: "Purpose", type: "text", full: true },
    { key: "amount", label: "Amount (₹)", type: "number", required: true },
    { key: "interest_rate", label: "Interest Rate (%)", type: "number" },
    { key: "applied_date", label: "Applied Date", type: "date" },
    { key: "status", label: "Status", type: "select", options: ["Applied", "Under Review", "Approved", "Disbursed", "Rejected"], required: true },
    { key: "disbursement_date", label: "Approval / Disbursement Date", type: "date" },
    { key: "renewal_date", label: "Renewal Date", type: "date" },
    { key: "remarks", label: "Remarks", type: "textarea", full: true },
  ];

  const renderTop = () => (
    <div>
      <div className="flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-[#C9A24B]" /><h3 className="font-heading font-medium">Bank / Investor Relationships</h3></div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lender-relationships">
        {rel.map((l) => (
          <div key={l.lender} className="rounded-xl border border-[#334155] bg-[#1E293B] p-5">
            <div className="font-heading font-semibold">{l.lender}</div>
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Total Exposure</span><span className="tabular-nums font-medium">{inr(l.total_exposure)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Avg Cost of Funds</span><span className="tabular-nums">{l.avg_cost_of_funds ? `${l.avg_cost_of_funds}%` : "—"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Facilities</span><span>{l.count}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Next Renewal</span><span>{l.next_renewal || "—"}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <CrudTable
      testid="funds" endpoint="funds"
      title="Fund Arrangements & Loans" subtitle="Track loan and investor pipelines while actively managing lender relationships, cost of funds, and renewal dates."
      addLabel="Add Fund / Loan" columns={columns} fields={fields}
      defaults={{ status: "Applied" }} renderTop={renderTop} onChanged={loadRel}
    />
  );
}
